import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { useCurrentOrganization } from "@/core/hooks/use-current-organization";
import type { TaxonomyProjectType } from "@/openapi";
import {
  archiveStatementTaxonomyMutation,
  createStatementTaxonomyMutation,
  listStatementTaxonomiesOptions,
  listStatementTaxonomiesQueryKey,
  publishStatementTaxonomyMutation,
  readDefaultStatementTaxonomyOptions,
  readProjectStatementTaxonomyOptions,
  readProjectStatementTaxonomyQueryKey,
  selectProjectStatementTaxonomyMutation,
  readStatementTaxonomyOptions,
  updateStatementTaxonomyMutation,
} from "@/openapi/@tanstack/react-query.gen";

/** Request the consumer's own statement-taxonomy library. */
export function useStatementTaxonomies(projectType: TaxonomyProjectType) {
  const { organizationId } = useCurrentOrganization({ throw: true });
  return useQuery(
    listStatementTaxonomiesOptions({
      path: { org_id: organizationId },
      query: { project_type: projectType },
    }),
  );
}

/** A null id means the packaged default, which lives behind its own endpoint. */
export function useStatementTaxonomy(taxonomyId: string | null) {
  const { organizationId } = useCurrentOrganization({ throw: true });
  const owned = useQuery({
    ...readStatementTaxonomyOptions({
      path: { org_id: organizationId, taxonomy_id: taxonomyId! },
    }),
    enabled: !!taxonomyId,
  });
  const packaged = useQuery({
    ...readDefaultStatementTaxonomyOptions({
      path: { org_id: organizationId },
    }),
    enabled: !taxonomyId,
  });
  return taxonomyId ? owned : packaged;
}

/** Mutations over one organization's taxonomy library, each refreshing the list. */
export function useStatementTaxonomyMutations() {
  const { organizationId } = useCurrentOrganization({ throw: true });
  const queryClient = useQueryClient();

  const invalidate = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: listStatementTaxonomiesQueryKey({
          path: { org_id: organizationId },
        }),
      }),
    [organizationId, queryClient],
  );

  const create = useMutation({
    ...createStatementTaxonomyMutation(),
    onSuccess: invalidate,
  });
  const update = useMutation({
    ...updateStatementTaxonomyMutation(),
    onSuccess: invalidate,
  });
  const publish = useMutation({
    ...publishStatementTaxonomyMutation(),
    onSuccess: invalidate,
  });
  const archive = useMutation({
    ...archiveStatementTaxonomyMutation(),
    onSuccess: invalidate,
  });

  return { organizationId, create, update, publish, archive };
}

/** The taxonomy one project's runs are built from, plus the ability to change it. */
export function useProjectStatementTaxonomy(projectId: string) {
  const { organizationId } = useCurrentOrganization({ throw: true });
  const queryClient = useQueryClient();

  const query = useQuery(
    readProjectStatementTaxonomyOptions({
      path: { org_id: organizationId, project_id: projectId },
    }),
  );

  const select = useMutation({
    ...selectProjectStatementTaxonomyMutation(),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: readProjectStatementTaxonomyQueryKey({
          path: { org_id: organizationId, project_id: projectId },
        }),
      }),
  });

  return { organizationId, query, select };
}
